import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import * as React from "react";
import {
  type ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import { ExtendedTable } from "@/components/ExtendedTable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  citedDomainsColumns,
  citedPagesColumns,
  exportToCSV,
} from "../services/helpers";
import { useOutletContext } from "react-router";
import type { Brand } from "@/components/AppSidebar.tsx";
import { useQuery } from "@tanstack/react-query";
import {
  competitorsAPI,
  type PaginatedResponseMeta,
  visabilityAPI,
} from "@/services/api.tsx";
import { useLocation, useNavigate } from "react-router-dom";
import { Download, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { useVisibilityRealtime } from "@/hooks/useVisibilityRealtime";
import { useCompetitorsRealtime } from "@/hooks/useCompetitorsRealtime";
import { canonicalizeDomain } from "@/utils/domain";
import type { DateRangeValue } from "@/components/DateRangeFilter";
import {
  DOMAIN_TYPE_OPTIONS,
  normalizeUrlTypeValue,
  toUrlTypeLabel,
} from "@/lib/urlTypes";
import {
  capturePosthogEvent,
  getTimeSinceBrandOnboardingSeconds,
  isFirstDashboardView,
} from "@/lib/posthog";
import { useAuth } from "@/components/AuthContext";
import { buildBrandKeyVariants, normalizeBrandKey } from "@/lib/brandAliases";

export type Prompt = {
  id: string;
  aiVisibility: number;
  prompt: string;
  location: string;
  createdAt: Date;
};

export { DOMAIN_TYPE_OPTIONS };

const defaultData = {
  citedDomainsVisibility: {},
  citedPagesStats: {},
  mentionedPagesStats: {},
  hasPendingExecutions: false,
};
const defaultCompetitors: any[] = [];
const defaultPaginationMeta: PaginatedResponseMeta = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};
const ALL_TYPE_FILTER_VALUE = "all";
const CITED_PAGES_CACHE_PREFIX = "nobori:cited-pages-cache:v1";
const CITED_PAGES_CACHE_TTL_MS = 1000 * 60 * 60 * 6;

const isPendingExecutionStatus = (status?: string | null): boolean => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  return (
    normalized === "running" ||
    normalized === "queued" ||
    normalized === "snapshot_pending" ||
    normalized === "processing" ||
    normalized === "in_progress" ||
    normalized === "in progress" ||
    normalized === "never_run" ||
    normalized === "not_triggered"
  );
};

const hasPendingExecutions = (runs: unknown): boolean => {
  if (!Array.isArray(runs)) return false;
  return runs.some((run: any) => {
    const responseText = String(run?.responseText || "")
      .trim()
      .toLowerCase();
    if (responseText === "in progress") return true;
    const statuses = run?.promptProviderStatuses;
    if (statuses && typeof statuses === "object") {
      return Object.values(statuses as Record<string, unknown>).some((status) =>
        isPendingExecutionStatus(String(status || "")),
      );
    }
    return false;
  });
};

type VisibilityCacheEntry = {
  savedAt: number;
  data: Record<string, any>;
};

const buildVisibilityCacheKey = ({
  brandKey,
  days,
  providersKey,
}: {
  brandKey: string;
  days: number;
  providersKey: string;
}) => `${CITED_PAGES_CACHE_PREFIX}:${brandKey}:${days}:${providersKey}`;

const readVisibilityCache = (cacheKey: string) => {
  if (!cacheKey || typeof window === "undefined") return undefined;
  try {
    const raw = window.localStorage.getItem(cacheKey);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as VisibilityCacheEntry;
    if (
      !parsed ||
      typeof parsed !== "object" ||
      typeof parsed.savedAt !== "number" ||
      !parsed.data
    ) {
      window.localStorage.removeItem(cacheKey);
      return undefined;
    }
    if (Date.now() - parsed.savedAt > CITED_PAGES_CACHE_TTL_MS) {
      window.localStorage.removeItem(cacheKey);
      return undefined;
    }
    return parsed.data;
  } catch {
    return undefined;
  }
};

const writeVisibilityCache = (
  cacheKey: string,
  data: Record<string, any> | undefined,
) => {
  if (!cacheKey || !data || typeof window === "undefined") return;
  try {
    const payload: VisibilityCacheEntry = {
      savedAt: Date.now(),
      data,
    };
    window.localStorage.setItem(cacheKey, JSON.stringify(payload));
  } catch {
    // Ignore cache write errors.
  }
};

const normalizeCompetitorsData = (payload: unknown): any[] => {
  if (Array.isArray(payload)) return payload;
  if (
    payload &&
    typeof payload === "object" &&
    Array.isArray((payload as { competitors?: unknown }).competitors)
  ) {
    return (payload as { competitors: any[] }).competitors;
  }
  return [];
};

export const normalizeTypeFilters = (values?: string[] | null): string[] => {
  const normalized = Array.from(
    new Set(
      (values || []).map((value) => String(value || "").trim()).filter(Boolean),
    ),
  );
  const specific = normalized.filter(
    (value) => value !== ALL_TYPE_FILTER_VALUE,
  );
  if (specific.length) return specific;
  return [ALL_TYPE_FILTER_VALUE];
};

export const matchesTypeFilter = (
  value: string,
  selectedFilters: string[],
): boolean => {
  const normalized = normalizeTypeFilters(selectedFilters);
  if (normalized.includes(ALL_TYPE_FILTER_VALUE)) return true;
  return normalized.includes(value);
};

export const filterCitedDomainRows = <
  T extends {
    domain: string;
    type: string;
  },
>(
  rows: T[],
  search: string,
  typeFilter: string[],
): T[] => {
  const normalizedSearch = search.trim().toLowerCase();
  return rows.filter((row) => {
    const matchesSearch = row.domain.toLowerCase().includes(normalizedSearch);
    const matchesType = matchesTypeFilter(row.type, typeFilter);
    return matchesSearch && matchesType;
  });
};

export const normalizeCitedDomainType = (
  value?: string | null,
  domainLike?: string | null,
) => {
  const type = String(value || "")
    .trim()
    .toLowerCase();
  const domain = canonicalizeDomain(domainLike || "") || "";
  if (
    domain === "youtube.com" ||
    domain.endsWith(".youtube.com") ||
    domain === "youtu.be"
  ) {
    return "Community";
  }
  if (type.includes("competitor")) return "Competitor";
  if (type.includes("publisher")) return "Publisher";
  if (
    type.includes("community") ||
    type.includes("social") ||
    type.includes("forum") ||
    type.includes("ugc")
  ) {
    return "Community";
  }
  if (type.includes("owned") || type.includes("owner")) return "Owned";
  return "Other";
};

export const domainsMatch = (
  domain?: string | null,
  candidate?: string | null,
): boolean => {
  const normalizedDomain = canonicalizeDomain(domain || "") || "";
  const normalizedCandidate = canonicalizeDomain(candidate || "") || "";
  if (!normalizedDomain || !normalizedCandidate) return false;
  return (
    normalizedDomain === normalizedCandidate ||
    normalizedDomain.endsWith(`.${normalizedCandidate}`)
  );
};

const hasMatchingDomain = (
  domainLike?: string | null,
  candidates?: Set<string>,
): boolean => {
  if (!candidates?.size) return false;
  const domain = canonicalizeDomain(domainLike || "") || "";
  if (!domain) return false;
  if (candidates.has(domain)) return true;
  return Array.from(candidates).some((candidate) =>
    domainsMatch(domain, candidate),
  );
};

export const buildTrackedCompetitorDomains = (competitorsData: any[]) => {
  const set = new Set<string>();
  competitorsData.forEach((competitor: any) => {
    if (competitor?.isOwnBrand) return;
    const status = String(competitor?.status || "")
      .trim()
      .toLowerCase();
    if (status && status !== "tracked") return;
    const domain = canonicalizeDomain(competitor?.domain || "");
    if (domain) {
      set.add(domain);
    }
  });
  return set;
};

export const buildOwnBrandDomains = (
  currentBrandUrl?: string | null,
  competitorsData: any[] = [],
) => {
  const set = new Set<string>();
  const currentDomain = canonicalizeDomain(currentBrandUrl || "");
  if (currentDomain) {
    set.add(currentDomain);
  }
  competitorsData.forEach((competitor: any) => {
    if (!competitor?.isOwnBrand && !competitor?.isMain) return;
    const domain = canonicalizeDomain(competitor?.domain || "");
    if (domain) {
      set.add(domain);
    }
  });
  return set;
};

export const resolveCitedSourceType = ({
  type,
  domainLike,
  ownBrandDomains,
  trackedCompetitorDomains,
}: {
  type?: string | null;
  domainLike?: string | null;
  ownBrandDomains: Set<string>;
  trackedCompetitorDomains: Set<string>;
}) => {
  if (hasMatchingDomain(domainLike, ownBrandDomains)) {
    return "Owned";
  }
  if (hasMatchingDomain(domainLike, trackedCompetitorDomains)) {
    return "Competitor";
  }
  return normalizeCitedDomainType(type, domainLike);
};

type MentionedBrandRef = {
  id: string;
  name?: string;
  key?: string;
  url?: string;
  isMain?: boolean;
};

const isTrackedCompetitorStatus = (status?: string | null): boolean => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  return !normalized || normalized === "tracked";
};

const brandRefIdentity = (brand: MentionedBrandRef): string => {
  const id = String(brand?.id || "")
    .trim()
    .toLowerCase();
  if (id) return `id:${id}`;
  const url = canonicalizeDomain(brand?.url || "");
  if (url) return `url:${url}`;
  const key = String(brand?.key || "")
    .trim()
    .toLowerCase();
  if (key) return `key:${key}`;
  const name = String(brand?.name || "")
    .trim()
    .toLowerCase();
  if (name) return `name:${name}`;
  return "";
};

const mergeMentionedBrandRefs = (
  base: MentionedBrandRef[] | undefined,
  inferred: MentionedBrandRef[],
): MentionedBrandRef[] => {
  const merged: MentionedBrandRef[] = Array.isArray(base) ? [...base] : [];
  const seen = new Set(
    merged.map((brand) => brandRefIdentity(brand)).filter(Boolean),
  );
  inferred.forEach((brand) => {
    const identity = brandRefIdentity(brand);
    if (!identity || seen.has(identity)) return;
    merged.push(brand);
    seen.add(identity);
  });
  return merged;
};

const buildCurrentBrandMentionKeys = (
  currentBrand?: SourceRowsContext["currentBrand"],
  ownBrandDomains: Set<string> = new Set(),
) => {
  const set = new Set<string>();
  [
    currentBrand?.name,
    currentBrand?.url,
    ...Array.from(ownBrandDomains),
  ].forEach((value) => {
    buildBrandKeyVariants(value).forEach((variant) => {
      if (variant) set.add(variant);
    });
    const domain = canonicalizeDomain(value || "");
    if (domain) {
      set.add(normalizeBrandKey(domain));
      set.add(normalizeBrandKey(domain.split(".")[0] || ""));
    }
  });
  return set;
};

const brandRefMatchesCurrentBrand = (
  brandRef: MentionedBrandRef,
  currentBrandKeys: Set<string>,
  ownBrandDomains: Set<string>,
) => {
  if (!brandRef) return false;
  if (brandRef.isMain === true) return true;
  const textValues = [
    brandRef.name,
    brandRef.key,
    (brandRef as any).brandName,
    (brandRef as any).brandDisplay,
  ];
  if (
    textValues.some((value) =>
      buildBrandKeyVariants(value).some((variant) =>
        currentBrandKeys.has(variant),
      ),
    )
  ) {
    return true;
  }
  const domainValues = [
    brandRef.url,
    (brandRef as any).domain,
    (brandRef as any).website,
    brandRef.key,
    brandRef.name,
  ];
  return domainValues.some((value) => {
    const domain = canonicalizeDomain(value || "");
    if (!domain) return false;
    return Array.from(ownBrandDomains).some((ownDomain) =>
      domainsMatch(domain, ownDomain),
    );
  });
};

export const inferMentionedBrandsFromDomain = ({
  pageUrl,
  currentBrand,
  competitorsData,
}: {
  pageUrl?: string | null;
  currentBrand?: {
    brandId?: string;
    id?: string;
    name?: string;
    url?: string;
  } | null;
  competitorsData: any[];
}): MentionedBrandRef[] => {
  const inferred: MentionedBrandRef[] = [];
  const pageDomain = canonicalizeDomain(pageUrl || "") || "";
  if (!pageDomain) return inferred;

  const ownBrandDomains = buildOwnBrandDomains(
    currentBrand?.url,
    competitorsData,
  );
  if (hasMatchingDomain(pageDomain, ownBrandDomains)) {
    const mainDomain =
      canonicalizeDomain(currentBrand?.url || "") || pageDomain;
    inferred.push({
      id: `main:${currentBrand?.brandId || currentBrand?.id || mainDomain}`,
      name: currentBrand?.name || mainDomain,
      key: currentBrand?.name || mainDomain,
      url: currentBrand?.url || mainDomain,
      isMain: true,
    });
  }

  competitorsData.forEach((competitor: any) => {
    if (competitor?.isOwnBrand || competitor?.isMain) return;
    if (!isTrackedCompetitorStatus(competitor?.status)) return;
    const competitorDomain = canonicalizeDomain(competitor?.domain || "");
    if (!competitorDomain || !domainsMatch(pageDomain, competitorDomain))
      return;
    inferred.push({
      id: String(competitor?.id || `competitor:${competitorDomain}`),
      name:
        String(
          competitor?.brandDisplay || competitor?.brandKey || competitorDomain,
        ).trim() || competitorDomain,
      key: String(competitor?.brandKey || "").trim() || competitorDomain,
      url:
        String(competitor?.domain || competitorDomain).trim() ||
        competitorDomain,
      isMain: false,
    });
  });

  return inferred;
};

type SourceRowsContext = {
  sources: any[];
  pagination: PaginationState;
  currentBrand?: {
    brandId?: string;
    id?: string;
    name?: string;
    url?: string;
  } | null;
  competitorsData: any[];
  resolveSourceType: (
    type?: string | null,
    domainLike?: string | null,
  ) => string;
};

export const buildCitedPageRows = ({
  sources,
  pagination,
  currentBrand,
  competitorsData,
  resolveSourceType,
}: SourceRowsContext) => {
  const pageIndex = Number.isFinite(pagination.pageIndex)
    ? Math.max(0, pagination.pageIndex)
    : 0;
  const pageSize = Number.isFinite(pagination.pageSize)
    ? Math.max(0, pagination.pageSize)
    : 0;
  const rowOffset = pageIndex * pageSize;
  const ownBrandDomains = buildOwnBrandDomains(
    currentBrand?.url,
    competitorsData,
  );
  const currentBrandKeys = buildCurrentBrandMentionKeys(
    currentBrand,
    ownBrandDomains,
  );

  return (Array.isArray(sources) ? sources : []).map(
    (source: any, index: number) => {
      const page = String(source?.url || "");
      const inferredMentionedBrands = inferMentionedBrandsFromDomain({
        pageUrl: page,
        currentBrand: {
          brandId: currentBrand?.brandId,
          id: currentBrand?.id,
          name: currentBrand?.name,
          url: currentBrand?.url,
        },
        competitorsData,
      });
      const mentionedBrands = mergeMentionedBrandRefs(
        source?.mentionedBrands,
        inferredMentionedBrands,
      );
      const rawUrlType = normalizeUrlTypeValue(source?.urlType);
      return {
        rowNumber: rowOffset + index + 1,
        brandId: currentBrand?.brandId || currentBrand?.id || "",
        url: page,
        used: source?.used,
        type: toUrlTypeLabel(rawUrlType),
        urlType: rawUrlType,
        domainType: resolveSourceType(source?.domainType, page),
        mentioned: mentionedBrands.some((brand) =>
          brandRefMatchesCurrentBrand(brand, currentBrandKeys, ownBrandDomains),
        ),
        mentionedBrands,
        updatedAt: source?.updatedAt ?? null,
        isSnapshotStale: Boolean(source?.isSnapshotStale),
        isSnapshotPending: Boolean(source?.isSnapshotPending),
        isManualRefreshRateLimited: Boolean(source?.isManualRefreshRateLimited),
        manualRefreshAvailableAt: source?.manualRefreshAvailableAt ?? null,
        snapshotDetails: source?.snapshotDetails ?? null,
        youtubeDetails: source?.youtubeDetails ?? null,
        platforms: Array.isArray(source?.platforms) ? source.platforms : [],
      };
    },
  );
};

export const buildCitedDomainRows = ({
  sources,
  pagination,
  resolveSourceType,
}: {
  sources: any[];
  pagination: PaginationState;
  resolveSourceType: (
    type?: string | null,
    domainLike?: string | null,
  ) => string;
}) => {
  const pageIndex = Number.isFinite(pagination.pageIndex)
    ? Math.max(0, pagination.pageIndex)
    : 0;
  const pageSize = Number.isFinite(pagination.pageSize)
    ? Math.max(0, pagination.pageSize)
    : 0;
  const rowOffset = pageIndex * pageSize;
  return (Array.isArray(sources) ? sources : []).map(
    (source: any, index: number) => {
      const domain =
        canonicalizeDomain(source?.domain || "") || source?.domain || "";
      const type = resolveSourceType(
        source?.domainType || source?.type,
        domain,
      );
      return {
        rowNumber: rowOffset + index + 1,
        domain,
        used: Number(source?.used || 0).toFixed(0),
        type,
        domainType: type,
        platforms: Array.isArray(source?.platforms) ? source.platforms : [],
      };
    },
  );
};

export default function CitationsPage() {
  const { t } = useLocale();
  const { user } = useAuth();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [sorting2, setSorting2] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnFilters2, setColumnFilters2] =
    React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [columnVisibility2, setColumnVisibility2] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [rowSelection2, setRowSelection2] = React.useState({});
  const [pagesPagination, setPagesPagination] = React.useState<PaginationState>(
    {
      pageIndex: 0,
      pageSize: 10,
    },
  );
  const [mentionedPagesPagination, setMentionedPagesPagination] =
    React.useState<PaginationState>({
      pageIndex: 0,
      pageSize: 10,
    });
  const [domainsPagination, setDomainsPagination] =
    React.useState<PaginationState>({
      pageIndex: 0,
      pageSize: 10,
    });
  const {
    currentBrand,
    selectedProviders,
    dateRange,
    citationsUrlTypeFilter,
    setCitationsUrlTypeFilter,
    citationsDomainTypeFilter,
    setCitationsDomainTypeFilter,
  } = useOutletContext<{
    currentBrand: Brand;
    selectedCountries: string[];
    selectedProviders?: string[];
    dateRange?: DateRangeValue;
    citationsUrlTypeFilter: string[];
    setCitationsUrlTypeFilter: (value: string[]) => void;
    citationsDomainTypeFilter: string[];
    setCitationsDomainTypeFilter: (value: string[]) => void;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = location.pathname.split("/").pop();
  const currentTab = activeTab === "citations" ? "cited-pages" : activeTab;
  const isCitedPagesTab = currentTab === "cited-pages";
  const isCitedDomainsTab = currentTab === "cited-domains";
  const isMentionedPagesTab = currentTab === "mentioned-pages";
  const [search, setSearch] = React.useState("");
  const [isExporting, setIsExporting] = React.useState(false);
  const [dashboardTrackedKey, setDashboardTrackedKey] = React.useState("");
  const [urlTypedTrackedKey, setUrlTypedTrackedKey] = React.useState("");
  const normalizedDays = dateRange?.days || 7;
  const requestDateRange = React.useMemo(
    () =>
      dateRange?.preset === "custom" || dateRange?.preset === "yesterday"
        ? {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          }
        : undefined,
    [dateRange?.endDate, dateRange?.preset, dateRange?.startDate],
  );
  const dateRangeKey =
    requestDateRange?.startDate && requestDateRange?.endDate
      ? `${requestDateRange.startDate}:${requestDateRange.endDate}`
      : `${normalizedDays}d`;
  const providersKey = selectedProviders?.join(",") || "all";
  const brandCacheKey = currentBrand?.brandId || currentBrand?.id || "";
  const visibilityQueryKey = [
    "visability",
    currentBrand?.brandId,
    normalizedDays,
    dateRangeKey,
    providersKey,
  ];
  const visibilityCacheKey = React.useMemo(
    () =>
      brandCacheKey
        ? buildVisibilityCacheKey({
            brandKey: brandCacheKey,
            days: normalizedDays,
            providersKey: `${providersKey}:${dateRangeKey}`,
          })
        : "",
    [brandCacheKey, dateRangeKey, normalizedDays, providersKey],
  );
  const brandSearch =
    currentBrand?.brandId || currentBrand?.id
      ? `?brandId=${currentBrand.brandId || currentBrand.id}`
      : location.search || "";
  const visibilityQuery = useQuery({
    queryKey: visibilityQueryKey,
    queryFn: async () => {
      const res = await visabilityAPI.getRunResults(
        currentBrand,
        normalizedDays,
        selectedProviders,
        undefined,
        false,
        false,
        requestDateRange,
        false,
        false,
      );
      writeVisibilityCache(visibilityCacheKey, res.data);
      return res.data as Record<string, any>;
    },
    enabled: !!currentBrand?.brandId,
    staleTime: Infinity,
    gcTime: Infinity,
    initialData: () => readVisibilityCache(visibilityCacheKey),
  });
  const data = visibilityQuery.data ?? defaultData;
  const { isFetched } = visibilityQuery;
  const sourcesSearch = search.trim();
  const activeUrlTypeFilters = React.useMemo(
    () =>
      normalizeTypeFilters(citationsUrlTypeFilter).filter(
        (value) => value !== ALL_TYPE_FILTER_VALUE,
      ),
    [citationsUrlTypeFilter],
  );
  const urlTypesKey = activeUrlTypeFilters.join(",") || "all";
  const activeDomainTypeFilters = React.useMemo(
    () =>
      normalizeTypeFilters(citationsDomainTypeFilter).filter(
        (value) => value !== ALL_TYPE_FILTER_VALUE,
      ),
    [citationsDomainTypeFilter],
  );
  const domainTypesKey = activeDomainTypeFilters.join(",") || "all";
  const citedPagesQuery = useQuery({
    queryKey: [
      "visibilitySources",
      "cited",
      currentBrand?.brandId,
      normalizedDays,
      dateRangeKey,
      providersKey,
      pagesPagination.pageIndex,
      pagesPagination.pageSize,
      sourcesSearch,
      urlTypesKey,
    ],
    queryFn: () =>
      visabilityAPI.getPromptSources({
        brandId: currentBrand.brandId,
        days: normalizedDays,
        providers: selectedProviders,
        page: pagesPagination.pageIndex + 1,
        pageSize: pagesPagination.pageSize,
        search: sourcesSearch,
        sourceKind: "cited",
        dateRange: requestDateRange,
        urlTypes: activeUrlTypeFilters,
      }),
    enabled: !!currentBrand?.brandId && isCitedPagesTab,
    staleTime: Infinity,
  });
  const citedDomainsQuery = useQuery({
    queryKey: [
      "visibilitySources",
      "cited-domains",
      currentBrand?.brandId,
      normalizedDays,
      dateRangeKey,
      providersKey,
      domainsPagination.pageIndex,
      domainsPagination.pageSize,
      sourcesSearch,
      domainTypesKey,
    ],
    queryFn: () =>
      visabilityAPI.getPromptSources({
        brandId: currentBrand.brandId,
        days: normalizedDays,
        providers: selectedProviders,
        page: domainsPagination.pageIndex + 1,
        pageSize: domainsPagination.pageSize,
        search: sourcesSearch,
        sourceKind: "cited-domains",
        dateRange: requestDateRange,
        domainTypes: activeDomainTypeFilters,
      }),
    enabled: !!currentBrand?.brandId && isCitedDomainsTab,
    staleTime: Infinity,
  });
  const mentionedPagesQuery = useQuery({
    queryKey: [
      "visibilitySources",
      "mentioned",
      currentBrand?.brandId,
      normalizedDays,
      dateRangeKey,
      providersKey,
      mentionedPagesPagination.pageIndex,
      mentionedPagesPagination.pageSize,
      sourcesSearch,
      urlTypesKey,
    ],
    queryFn: () =>
      visabilityAPI.getPromptSources({
        brandId: currentBrand.brandId,
        days: normalizedDays,
        providers: selectedProviders,
        page: mentionedPagesPagination.pageIndex + 1,
        pageSize: mentionedPagesPagination.pageSize,
        search: sourcesSearch,
        sourceKind: "mentioned",
        dateRange: requestDateRange,
        urlTypes: activeUrlTypeFilters,
      }),
    enabled: !!currentBrand?.brandId && isMentionedPagesTab,
    staleTime: Infinity,
  });
  const citedPagesPaginationMeta =
    citedPagesQuery.data?.pagination || defaultPaginationMeta;
  const citedDomainsPaginationMeta =
    citedDomainsQuery.data?.pagination || defaultPaginationMeta;
  const mentionedPagesPaginationMeta =
    mentionedPagesQuery.data?.pagination || defaultPaginationMeta;

  const hasCitationData =
    citedPagesPaginationMeta.total > 0 ||
    mentionedPagesPaginationMeta.total > 0 ||
    citedDomainsPaginationMeta.total > 0;
  const hasPendingRunExecutions =
    Boolean((data as any)?.hasPendingExecutions) ||
    hasPendingExecutions((data as any).data);
  const pendingExecutionsLoading = !hasCitationData && hasPendingRunExecutions;
  const citedPagesLoading =
    citedPagesQuery.isLoading || pendingExecutionsLoading;
  const mentionedPagesLoading =
    mentionedPagesQuery.isLoading || pendingExecutionsLoading;
  const citedDomainsLoading =
    citedDomainsQuery.isLoading || pendingExecutionsLoading;
  const activeTableLoading =
    currentTab === "cited-domains"
      ? citedDomainsLoading
      : currentTab === "mentioned-pages"
        ? mentionedPagesLoading
        : citedPagesLoading;

  React.useEffect(() => {
    setPagesPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
    setMentionedPagesPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
    setDomainsPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
  }, [
    currentBrand?.brandId,
    dateRangeKey,
    domainTypesKey,
    providersKey,
    sourcesSearch,
    urlTypesKey,
  ]);

  React.useEffect(() => {
    if (!visibilityCacheKey || !isFetched || !visibilityQuery.data) return;
    writeVisibilityCache(visibilityCacheKey, visibilityQuery.data);
  }, [visibilityCacheKey, isFetched, visibilityQuery.data]);

  const { data: competitorsData = defaultCompetitors } = useQuery({
    queryKey: ["competitors", currentBrand?.brandId, providersKey],
    queryFn: async () => {
      const res = await competitorsAPI.getCompetitors(
        currentBrand.brandId,
        selectedProviders,
      );
      return res.competitors || [];
    },
    select: normalizeCompetitorsData,
    enabled: !!currentBrand?.brandId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const trackedCompetitorDomains = React.useMemo(
    () => buildTrackedCompetitorDomains(competitorsData),
    [competitorsData],
  );

  const ownBrandDomains = React.useMemo(
    () => buildOwnBrandDomains(currentBrand?.url, competitorsData),
    [competitorsData, currentBrand?.url],
  );

  const resolveSourceType = React.useCallback(
    (type?: string | null, domainLike?: string | null) => {
      return resolveCitedSourceType({
        type,
        domainLike,
        ownBrandDomains,
        trackedCompetitorDomains,
      });
    },
    [ownBrandDomains, trackedCompetitorDomains],
  );

  useVisibilityRealtime({
    brandId: currentBrand?.brandId,
    days: normalizedDays,
    providers: selectedProviders,
    queryKey: visibilityQueryKey,
    enabled: !!currentBrand?.brandId,
  });

  useCompetitorsRealtime({
    brandId: currentBrand?.brandId,
    providers: selectedProviders,
    queryKey: ["competitors", currentBrand?.brandId, providersKey],
    enabled: !!currentBrand?.brandId,
  });

  const domainsData: any = React.useMemo(() => {
    return buildCitedDomainRows({
      sources: citedDomainsQuery.data?.sources || [],
      pagination: domainsPagination,
      resolveSourceType,
    });
  }, [citedDomainsQuery.data?.sources, domainsPagination, resolveSourceType]);

  const pagesData = React.useMemo(() => {
    return buildCitedPageRows({
      sources: citedPagesQuery.data?.sources || [],
      pagination: pagesPagination,
      currentBrand: {
        brandId: currentBrand?.brandId,
        id: currentBrand?.id,
        name: currentBrand?.name,
        url: currentBrand?.url,
      },
      competitorsData,
      resolveSourceType,
    });
  }, [
    citedPagesQuery.data?.sources,
    competitorsData,
    currentBrand?.brandId,
    currentBrand?.id,
    currentBrand?.name,
    currentBrand?.url,
    pagesPagination,
    resolveSourceType,
  ]);

  const mentionedPagesData = React.useMemo(() => {
    return buildCitedPageRows({
      sources: mentionedPagesQuery.data?.sources || [],
      pagination: mentionedPagesPagination,
      currentBrand: {
        brandId: currentBrand?.brandId,
        id: currentBrand?.id,
        name: currentBrand?.name,
        url: currentBrand?.url,
      },
      competitorsData,
      resolveSourceType,
    });
  }, [
    mentionedPagesQuery.data?.sources,
    competitorsData,
    currentBrand?.brandId,
    currentBrand?.id,
    currentBrand?.name,
    currentBrand?.url,
    mentionedPagesPagination,
    resolveSourceType,
  ]);

  React.useEffect(() => {
    setSearch("");
    setCitationsUrlTypeFilter([ALL_TYPE_FILTER_VALUE]);
    setCitationsDomainTypeFilter([ALL_TYPE_FILTER_VALUE]);
  }, [activeTab, setCitationsDomainTypeFilter, setCitationsUrlTypeFilter]);

  React.useEffect(() => {
    if (!currentBrand?.brandId || activeTableLoading) return;
    const hasData =
      citedPagesPaginationMeta.total > 0 ||
      mentionedPagesPaginationMeta.total > 0 ||
      citedDomainsPaginationMeta.total > 0;
    const key = `${currentBrand.brandId}:${hasData}`;
    if (dashboardTrackedKey === key) return;
    setDashboardTrackedKey(key);
    capturePosthogEvent("dashboard_viewed", {
      brand_id: currentBrand.brandId,
      dashboard: "citations",
      is_first_view: isFirstDashboardView({
        userId: user?.id,
        brandId: currentBrand.brandId,
        dashboard: "citations",
      }),
      has_data: hasData,
      time_since_onboarding_seconds:
        getTimeSinceBrandOnboardingSeconds(currentBrand.brandId) ?? undefined,
    });
  }, [
    currentBrand?.brandId,
    citedPagesPaginationMeta.total,
    citedDomainsPaginationMeta.total,
    mentionedPagesPaginationMeta.total,
    dashboardTrackedKey,
    activeTableLoading,
    user?.id,
  ]);

  React.useEffect(() => {
    if (!currentBrand?.brandId || citedPagesLoading) return;
    const countTyped = pagesData.filter((entry: any) =>
      Boolean(entry?.urlType),
    ).length;
    const key = `${currentBrand.brandId}:${countTyped}`;
    if (urlTypedTrackedKey === key) return;
    setUrlTypedTrackedKey(key);
    capturePosthogEvent("sources_url_typed", {
      brand_id: currentBrand.brandId,
      count_typed: countTyped,
    });
  }, [currentBrand?.brandId, pagesData, citedPagesLoading, urlTypedTrackedKey]);

  const domainsTable = useReactTable({
    data: domainsData,
    columns: citedDomainsColumns,
    onSortingChange: setSorting2,
    onColumnFiltersChange: setColumnFilters2,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: citedDomainsPaginationMeta.totalPages,
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility2,
    onRowSelectionChange: setRowSelection2,
    onPaginationChange: setDomainsPagination,
    state: {
      pagination: domainsPagination,
      sorting: sorting2,
      columnFilters: columnFilters2,
      columnVisibility: columnVisibility2,
      rowSelection: rowSelection2,
    },
  });

  const pagesTable = useReactTable({
    data: pagesData,
    columns: citedPagesColumns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: citedPagesPaginationMeta.totalPages,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagesPagination,
    state: {
      pagination: pagesPagination,
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  const mentionedPagesTable = useReactTable({
    data: mentionedPagesData,
    columns: citedPagesColumns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: mentionedPagesPaginationMeta.totalPages,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setMentionedPagesPagination,
    state: {
      pagination: mentionedPagesPagination,
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  const handleExport = React.useCallback(async () => {
    if (!currentBrand?.brandId) return;
    setIsExporting(true);
    try {
      const isDomainExport = currentTab === "cited-domains";
      const isMentionedExport = currentTab === "mentioned-pages";
      const response = await visabilityAPI.exportPromptSources({
        brandId: currentBrand.brandId,
        days: normalizedDays,
        providers: selectedProviders,
        search: sourcesSearch,
        sourceKind: isDomainExport
          ? "cited-domains"
          : isMentionedExport
            ? "mentioned"
            : "cited",
        dateRange: requestDateRange,
        urlTypes: isDomainExport ? undefined : activeUrlTypeFilters,
        domainTypes: isDomainExport ? activeDomainTypeFilters : undefined,
      });
      const exportRows = isDomainExport
        ? buildCitedDomainRows({
            sources: response.sources || [],
            pagination: {
              pageIndex: 0,
              pageSize: response.sources?.length || 0,
            },
            resolveSourceType,
          })
        : buildCitedPageRows({
            sources: response.sources || [],
            pagination: {
              pageIndex: 0,
              pageSize: response.sources?.length || 0,
            },
            currentBrand: {
              brandId: currentBrand?.brandId,
              id: currentBrand?.id,
              name: currentBrand?.name,
              url: currentBrand?.url,
            },
            competitorsData,
            resolveSourceType,
          });

      exportToCSV(exportRows, [], "sources.csv", {
        emptyMessage: t("legacy.there.is.no.data.for.export.ojj9kv"),
        successMessage: t("legacy.csv.is.downloaded.kdpi5p"),
      });
    } catch {
      exportToCSV([], [], "sources.csv", {
        emptyMessage: t("legacy.there.is.no.data.for.export.ojj9kv"),
        successMessage: t("legacy.csv.is.downloaded.kdpi5p"),
      });
    } finally {
      setIsExporting(false);
    }
  }, [
    activeDomainTypeFilters,
    activeUrlTypeFilters,
    competitorsData,
    currentBrand?.brandId,
    currentBrand?.id,
    currentBrand?.name,
    currentBrand?.url,
    currentTab,
    normalizedDays,
    requestDateRange,
    resolveSourceType,
    selectedProviders,
    sourcesSearch,
    t,
  ]);

  return (
    <div>
      <div className="text-muted-foreground mb-4 text-sm">
        <LocalizedText id="legacy.pages.and.domains.ai.platforms.cite.or.mention.utg4rx" />
      </div>
      <Tabs
        value={currentTab}
        onValueChange={(value) => {
          navigate(`/dashboard/citations/${value}${brandSearch}`);
        }}
        defaultValue="cited-pages"
      >
        <div className="w-full flex justify-between">
          <TabsList className="">
            <TabsTrigger className="cursor-pointer" value="cited-pages">
              <LocalizedText id="legacy.cited.pages.73qhui" />
            </TabsTrigger>
            <TabsTrigger className="cursor-pointer" value="cited-domains">
              <LocalizedText id="legacy.cited.domains.1iczww9" />
            </TabsTrigger>
          </TabsList>
          <div className="flex gap-2">
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                type="text"
                placeholder={t("legacy.search.14mmb9h")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 max-w-sm min-w-[200px]"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="px-0 cursor-pointer absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={18} />
                </button>
              )}
            </div>
            <Separator
              orientation="vertical"
              className="data-[orientation=vertical]:h-[35px]"
            />

            <Button
              className="cursor-pointer"
              variant="outline"
              onClick={handleExport}
              disabled={isExporting}
            >
              <Download></Download> <LocalizedText id="legacy.export.1305875" />
            </Button>
          </div>
        </div>
        <TabsContent value="cited-pages">
          <ExtendedTable
            loading={citedPagesLoading}
            pageName="citedPages"
            noDataText={{
              title: "No cited pages found yet",
              description:
                "We didn’t detect any pages where AI models cite your brand. Try again later or add more prompts to improve coverage.",
            }}
            rowRedirect={`/dashboard/get-cited${brandSearch}`}
            table={pagesTable}
            action={{
              actionFn: () => {
                navigate(`/dashboard/prompts${brandSearch}`);
              },
              title: "Add prompts",
            }}
            columns={citedPagesColumns.length}
          ></ExtendedTable>
        </TabsContent>
        <TabsContent value="mentioned-pages">
          <ExtendedTable
            loading={mentionedPagesLoading}
            pageName="citedPages"
            noDataText={{
              title: "No mentioned pages found yet",
              description:
                "We haven't detected any recommended brand pages yet. Try again after the next execution.",
            }}
            table={mentionedPagesTable}
            columns={citedPagesColumns.length}
          ></ExtendedTable>
        </TabsContent>
        <TabsContent value="cited-domains" className="w-full">
          <ExtendedTable
            loading={citedDomainsLoading}
            pageName="citedDomains"
            noDataText={{
              title: "No cited domains found yet",
              description:
                "We couldn’t find any domains cited by AI platforms for your brand yet. New sources will appear after the next execution.",
            }}
            table={domainsTable}
            columns={citedDomainsColumns.length}
          ></ExtendedTable>
        </TabsContent>
      </Tabs>
    </div>
  );
}
